import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem as MuiMenuItem,
  InputAdornment,
  IconButton,
  Chip,
  useTheme,
  Alert,
  Menu,
} from '@mui/material';
import {
  Search,
  Visibility,
  MoreVert,
} from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from 'material-react-table';
import { Business, BUSINESS_CATEGORIES } from '@/constants/types';
import { businessService } from '@/services/business/businessService';
import { useNavigate } from 'react-router-dom';

export const ManageBusinesses: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);

  useEffect(() => {
    loadBusinesses();
  }, [globalFilter, categoryFilter, statusFilter, paginationState]);

  const loadBusinesses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params: any = {
        page: paginationState.pageIndex + 1,
        limit: paginationState.pageSize,
        sort: '-createdAt',
      };

      if (globalFilter) params.search = globalFilter;
      if (categoryFilter !== 'all') params.businessCategory = categoryFilter;
      if (statusFilter !== 'all') params.approvalStatus = statusFilter;

      const response = await businessService.getAllBusinesses(params);
      setBusinesses(response.data.businesses);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load businesses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (businessId: string) => {
    navigate(`/admin/manage-business/${businessId}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, businessId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBusinessId(businessId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBusinessId(null);
  };

  const handleMenuAction = () => {
    if (selectedBusinessId) {
      handleViewDetails(selectedBusinessId);
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'in_review':
        return 'info';
      case 'clarification_needed':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const columns = useMemo<MRT_ColumnDef<Business>[]>(
    () => [
      {
        accessorKey: 'businessName',
        header: 'Business Name',
        size: 200,
        Cell: ({ cell, row }) => (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {cell.getValue<string>()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.businessUsername}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'businessCategory',
        header: 'Category',
        size: 200,
        Cell: ({ row }) => {
          const categories = row.original.businessCategory;
          if (Array.isArray(categories)) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {categories.map((category) => (
                  <Chip key={category} label={category} size="small" variant="outlined" />
                ))}
              </Box>
            );
          }
          return <Chip label={categories} size="small" variant="outlined" />;
        },
      },
      {
        accessorKey: 'city',
        header: 'Location',
        size: 150,
        Cell: ({ row }) => `${row.original.city}, ${row.original.state}`,
      },
      {
        accessorKey: 'ownerName',
        header: 'Owner',
        size: 150,
      },
      {
        accessorKey: 'approvalStatus',
        header: 'Status',
        size: 150,
        Cell: ({ cell }) => (
          <Chip
            label={getStatusLabel(cell.getValue<string>())}
            color={getStatusColor(cell.getValue<string>())}
            size="small"
          />
        ),
      },
      {
        accessorKey: 'isBlocked',
        header: 'Blocked',
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Yes' : 'No'}
            color={cell.getValue() ? 'error' : 'default'}
            size="small"
          />
        ),
      },
      {
        accessorKey: 'isSuspended',
        header: 'Suspended',
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Yes' : 'No'}
            color={cell.getValue() ? 'warning' : 'default'}
            size="small"
          />
        ),
      },
      {
        id: 'actions',
        header: 'Admin Actions',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, row.original._id)}
            title="Admin Actions"
          >
            <MoreVert fontSize="small" />
          </IconButton>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: businesses,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: pagination.totalItems,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleViewDetails(row.original._id),
      sx: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
    state: {
      isLoading,
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Manage Businesses
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search businesses..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 300 }}
          size="small"
        />

        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MuiMenuItem value="all">All Categories</MuiMenuItem>
          {BUSINESS_CATEGORIES.map((category) => (
            <MuiMenuItem key={category} value={category}>
              {category}
            </MuiMenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MuiMenuItem value="all">All Status</MuiMenuItem>
          <MuiMenuItem value="pending">Pending</MuiMenuItem>
          <MuiMenuItem value="in_review">In Review</MuiMenuItem>
          <MuiMenuItem value="clarification_needed">Clarification Needed</MuiMenuItem>
          <MuiMenuItem value="approved">Approved</MuiMenuItem>
          <MuiMenuItem value="rejected">Rejected</MuiMenuItem>
        </TextField>
      </Box>

      <MaterialReactTable table={table} />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MuiMenuItem onClick={handleMenuAction}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MuiMenuItem>
      </Menu>
    </Box>
  );
};
